import React, { useId } from "react";
import { cn } from "../utils/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  containerClassName?: string;
  labelClassName?: string;
  wrapperClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      wrapperClassName,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      onRightIconClick,
      id,
      required,
      disabled,
      type = "text",
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const hasError = Boolean(error);

    return (
      <div className={cn("flex flex-col gap-1.5", containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "text-sm font-medium text-gray-700",
              disabled && "opacity-50 cursor-not-allowed",
              labelClassName,
            )}
          >
            {label}
            {required && (
              <span className="ml-1 text-red-500" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <div className={cn("relative flex items-center", wrapperClassName)}>
          {leftIcon && (
            <span
              className={cn(
                "absolute left-3 flex items-center justify-center text-gray-400 pointer-events-none",
                hasError && "text-red-400",
              )}
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            aria-describedby={
              [
                hasError ? errorId : undefined,
                helperText ? helperId : undefined,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            className={cn(
              "w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm text-gray-900",
              "placeholder:text-gray-400",
              "transition-all duration-150",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              !hasError && [
                "border-gray-300",
                "hover:border-gray-400",
                "focus:border-blue-600 focus:ring-blue-100",
              ],
              hasError && [
                "border-red-400 bg-red-50",
                "focus:border-red-500 focus:ring-red-100",
              ],
              disabled && "opacity-50 cursor-not-allowed bg-gray-50",
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              className,
            )}
            {...props}
          />

          {rightIcon && (
            <span
              className={cn(
                "absolute right-3 flex items-center justify-center text-gray-400",
                onRightIconClick
                  ? "cursor-pointer hover:text-gray-600"
                  : "pointer-events-none",
              )}
              onClick={onRightIconClick}
              role={onRightIconClick ? "button" : undefined}
              tabIndex={onRightIconClick ? 0 : undefined}
              aria-hidden={!onRightIconClick}
            >
              {rightIcon}
            </span>
          )}
        </div>

        {hasError && (
          <p
            id={errorId}
            role="alert"
            className="flex items-center gap-1 text-xs text-red-600 font-medium"
          >
            <svg
              className="h-3.5 w-3.5 shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}

        {helperText && !hasError && (
          <p id={helperId} className="text-xs text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

// Day 9 complete
